import React, { useEffect, useRef, useState } from 'react';
import { Upload, ArrowDown, ArrowUp, Trash2, Download } from 'lucide-react';
import Swal from 'sweetalert2';
import { Tooltip } from '@mui/material';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import appConfig from '../../config';
import { deleteUploads, updateProperty, uploadFile } from '../../helper/backend_helper';

const ImageManager = ({ property }) => {
  const [loading, setLoading] = useState(false);
  const [mainPhoto, setMainPhoto] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [otherImages, setOtherImages] = useState([]);

  useEffect(() => {
    setMainPhoto(property?.mainPhoto || null);
    setSelectedPhotos(property?.selectedPhotos || []);
    setOtherImages(property?.otherImages || []);
  }, [property?.mainPhoto, property?.selectedPhotos, property?.otherImages]);

  const handleSaveListing = async () => {
    try {
      setLoading(true);

      console.log(mainPhoto);
      const uploadedMainPhoto =
        //   const submitData = {
        //     mainPhoto,
        //     selectedPhotos,
        //     otherImages,
        //   };

        //   await updateProperty(property?._id, submitData);
        Swal.fire({
          icon: 'success',
          title: 'Property listings updated successfully',
        });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update',
      });
    }
  };

  const handleUpload = async (e) => {
    const fileList = e.target.files;
    if (!fileList) return;

    const fileArray = Array.from(fileList); // convert FileList to array

    setLoading(true);

    try {
      const urls = await uploadFileArray(fileArray); // assume it returns array of URLs

      await updateProperty(property?._id, { otherImages: [...otherImages, ...urls] });
      setOtherImages((prev) => [...prev, ...urls]);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const moveToMain = async (url) => {
    if (mainPhoto) setOtherImages((prev) => [...prev, mainPhoto]);
    setMainPhoto(url);
    setSelectedPhotos((prev) => prev.filter((img) => img !== url));
    setOtherImages((prev) => prev.filter((img) => img !== url));
    const submitData = {
      mainPhoto: url,
      selectedPhotos: selectedPhotos.filter((img) => img !== url),
      otherImages: otherImages.filter((img) => img !== url),
    };

    await updateProperty(property?._id, submitData);
  };

  const moveToSelected = async (url) => {
    setSelectedPhotos((prev) => [...prev, url]);
    if (mainPhoto === url) setMainPhoto(null);
    setOtherImages((prev) => prev.filter((img) => img !== url));

    const submitData = {
      mainPhoto: mainPhoto === url ? null : mainPhoto,
      selectedPhotos: [...selectedPhotos, url],
      otherImages: otherImages.filter((img) => img !== url),
    };

    await updateProperty(property?._id, submitData);
  };

  const moveToOther = async (url) => {
    setOtherImages((prev) => [...prev, url]);
    setSelectedPhotos((prev) => prev.filter((img) => img !== url));
    if (mainPhoto === url) setMainPhoto(null);

    const submitData = {
      mainPhoto: mainPhoto === url ? null : mainPhoto,
      selectedPhotos: selectedPhotos.filter((img) => img !== url),
      otherImages: [...otherImages, url],
    };

    await updateProperty(property?._id, submitData);
  };

  const deleteImage = async (url) => {
    if (mainPhoto === url) setMainPhoto(null);
    setSelectedPhotos((prev) => prev.filter((img) => img !== url));
    setOtherImages((prev) => prev.filter((img) => img !== url));

    const submitData = {
      mainPhoto: mainPhoto === url ? null : mainPhoto,
      selectedPhotos: selectedPhotos.filter((img) => img !== url),
      otherImages: otherImages.filter((img) => img !== url),
    };
    await updateProperty(property?._id, submitData);
    await deleteUploads({ filename: url }, { headers: { 'Content-Type': 'application/json' } }); // You must define `deleteUploads`
  };

  const uploadFileArray = async (fileArray) => {
    console.log('uploadFileArray received:', fileArray);
    if (!Array.isArray(fileArray) || fileArray.length === 0) return [];

    const filesToUpload = fileArray.filter((file) => file instanceof File);

    if (filesToUpload.length === 0) return [];

    const uploadPromises = filesToUpload.map((file) =>
      uploadFile({ file }, { headers: { 'Content-Type': 'multipart/form-data' } })
    ); // You must define `uploadFile`
    return (await Promise.all(uploadPromises)).map((response) => response.data.data.fileName); // array of uploaded URLs or filenames
  };

  const renderImageCard = (url, position) => (
    <div key={url} className="relative w-full max-w-sm bg-gray-50 rounded overflow-hidden mb-4">
      <img src={appConfig.FILE_URL + url} alt="property" className="w-full h-56 object-cover" />
      <div className="absolute bottom-2 right-2 flex space-x-1">
        {position === 'selected' && (
          <button
            onClick={() => moveToMain(url)}
            className="p-2 bg-gray-400 bg-opacity-70 rounded-full hover:bg-gray-500"
          >
            <ArrowUp size={16} className="text-white" />
          </button>
        )}
        {position === 'other' && (
          <>
            <Tooltip title="Move to Main" arrow>
              <button
                onClick={() => moveToMain(url)}
                className="p-2 bg-gray-400 bg-opacity-70 rounded-full hover:bg-gray-500"
              >
                <ArrowUp size={16} className="text-white" />
              </button>
            </Tooltip>
            <button
              onClick={() => moveToSelected(url)}
              className="p-2 bg-gray-400 bg-opacity-70 rounded-full hover:bg-gray-500"
            >
              <ArrowUp size={16} className="text-white" />
            </button>
          </>
        )}
        {(position === 'main' || position === 'selected') && (
          <button
            onClick={() => moveToOther(url)}
            className="p-2 bg-gray-400 bg-opacity-70 rounded-full hover:bg-gray-500"
          >
            <ArrowDown size={16} className="text-white" />
          </button>
        )}
        <button
          onClick={() => deleteImage(url)}
          className="p-2 bg-gray-400 bg-opacity-70 rounded-full hover:bg-gray-500"
        >
          <Trash2 size={16} className="text-white" />
        </button>
      </div>
    </div>
  );
  const canvasRef = useRef(null);

  // const handleImageProcessing = async () => {
  //   // const bgUrl = 'http://localhost:5009/uploads/1748243214205hq720.jpg';
  //   const logoUrl = '/logo.png'; // Place your logo in public folder or use URL

  //   const allImages = [];

  //   // eslint-disable-next-line no-unused-expressions
  //   property?.mainPhoto && allImages.push(property?.mainPhoto);
  //   // eslint-disable-next-line no-unused-expressions
  //   property?.selectedPhotos && property?.selectedPhotos.forEach((img) => allImages.push(img));
  //   // eslint-disable-next-line no-unused-expressions
  //   property?.otherImages && property?.otherImages.forEach((img) => allImages.push(img));

  //   for (let i = 0; i < allImages.length; i += 1) {
  //     const bgUrl = appConfig.FILE_URL + allImages[i];
  //     console.log(bgUrl);
  //     const canvas = canvasRef.current;
  //     const ctx = canvas.getContext('2d');

  //     const bgImage = new Image();
  //     const logoImage = new Image();

  //     // Load both images
  //     bgImage.crossOrigin = 'Anonymous';
  //     logoImage.crossOrigin = 'Anonymous';

  //     bgImage.src = bgUrl;
  //     logoImage.src = logoUrl;

  //     bgImage.onload = () => {
  //       canvas.width = bgImage.width;
  //       canvas.height = bgImage.height;

  //       ctx.drawImage(bgImage, 0, 0); // Draw background image

  //       const logoWidth = bgImage.width * 0.2; // 20% of background width
  //       const logoHeight = (logoImage.height / logoImage.width) * logoWidth;

  //       // Draw logo at top-right
  //       ctx.drawImage(logoImage, bgImage.width - logoWidth - 20, 20, logoWidth, logoHeight);

  //       const link = document.createElement('a');
  //       link.download = `${property?.name}_${i + 1}.png`;
  //       link.href = canvasRef.current.toDataURL();
  //       link.click();
  //     };
  //   }
  // };

  const handleDownloadImagesWithLogo = async () => {
    const logoUrl = '/logo.png'; // Put logo.png in your public folder
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const zip = new JSZip();

    const loadImage = (src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    };

    const logoImage = await loadImage(logoUrl);

    const processImage = async (imageUrl, fileName) => {
      const bgImage = await loadImage(imageUrl);

      canvas.width = bgImage.width;
      canvas.height = bgImage.height;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bgImage, 0, 0);

      const logoWidth = bgImage.width * 0.2;
      const logoHeight = (logoImage.height / logoImage.width) * logoWidth;
      ctx.drawImage(logoImage, bgImage.width - logoWidth - 20, 20, logoWidth, logoHeight);

      const dataURL = canvas.toDataURL('image/png');
      const base64 = dataURL.replace(/^data:image\/(png|jpg);base64,/, '');
      zip.file(fileName, base64, { base64: true });
    };

    const FILE_URL = appConfig.FILE_URL;
    const propertyName = property?.name || 'property';

    // Main photo
    if (mainPhoto) {
      await processImage(FILE_URL + mainPhoto, 'main photo.png');
    }

    // Selected photos
    if (selectedPhotos) {
      for (let i = 0; i < selectedPhotos.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await processImage(FILE_URL + selectedPhotos[i], `selected photo ${i + 1}.png`);
      }
    }

    // Other images
    if (otherImages) {
      for (let i = 0; i < otherImages.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await processImage(FILE_URL + otherImages[i], `other image ${i + 1}.png`);
      }
    }

    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `${propertyName}_images_with_logo.zip`);
  };

  const handleDownloadOriginalImages = async () => {
    const zip = new JSZip();
    const FILE_URL = appConfig.FILE_URL;
    const propertyName = property?.name || 'property';

    const fetchAndAddToZip = async (url, fileName) => {
      // eslint-disable-next-line no-await-in-loop
      const response = await fetch(url);
      // eslint-disable-next-line no-await-in-loop
      const blob = await response.blob();
      zip.file(fileName, blob);
    };

    // Main photo
    if (mainPhoto) {
      // eslint-disable-next-line no-await-in-loop
      await fetchAndAddToZip(FILE_URL + mainPhoto, 'main photo.png');
    }

    // Selected photos
    if (selectedPhotos) {
      for (let i = 0; i < selectedPhotos.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await fetchAndAddToZip(FILE_URL + selectedPhotos[i], `selected photo ${i + 1}.png`);
      }
    }

    // Other images
    if (otherImages) {
      for (let i = 0; i < otherImages.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await fetchAndAddToZip(FILE_URL + otherImages[i], `other image ${i + 1}.png`);
      }
    }

    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `${propertyName}_original_images.zip`);
  };
  return (
    <div>
      {/* Upload & Actions */}
      {/* @ts-ignore */}
      <div className="flex flex-wrap gap-3 mb-6">
        <label
          htmlFor="file"
          className="flex items-center bg-indigo-600 text-white rounded px-4 py-2 hover:bg-indigo-700 cursor-pointer"
        >
          <Upload size={16} className="mr-2" />
          <span>Upload Images</span>
          <input id="file" type="file" multiple accept="image/*" onChange={handleUpload} style={{ display: 'none' }} />
        </label>

        <button
          className="flex items-center border border-gray-300 rounded px-4 py-2 hover:bg-gray-50"
          onClick={() => handleDownloadOriginalImages()}
        >
          <Download size={16} className="mr-2" />
          <span>Download All</span>
        </button>
        <button
          className="flex items-center border border-gray-300 rounded px-4 py-2 hover:bg-gray-50"
          onClick={() => handleDownloadImagesWithLogo()}
        >
          <Download size={16} className="mr-2" />
          <span>Download All - with logo</span>
        </button>
        {loading && <span className="flex items-center"> ⚠️ Uploading images please wait....!</span>}
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Main Photo */}
      <div className="mb-6">
        <h3 className="font-medium mb-3">Main Photo</h3>
        {mainPhoto ? renderImageCard(mainPhoto, 'main') : <p className="text-gray-500">No main photo selected</p>}
      </div>

      {/* Selected Photos */}
      <div className="mb-6">
        <h3 className="font-medium mb-3">Selected Photos</h3>
        {selectedPhotos.length > 0 ? (
          selectedPhotos.map((url) => renderImageCard(url, 'selected'))
        ) : (
          <p className="text-gray-500">No selected photos</p>
        )}
      </div>

      {/* Other Photos */}
      <div className="mb-6">
        <h3 className="font-medium mb-3">Other Photos</h3>
        {otherImages.length > 0 ? (
          otherImages.map((url) => renderImageCard(url, 'other'))
        ) : (
          <p className="text-gray-500">No other photos</p>
        )}
      </div>
    </div>
  );
};

export default ImageManager;
